import { alpha, Button, CircularProgress, Paper, Stack, ThemeProvider } from '@mui/material'
import { forwardRef, Fragment, useEffect, useImperativeHandle } from 'react'
import { forceUpdateCookies, isAndroidORIosApp } from 'webtonative'
import { logEvent, logScreen, setCollection, setDefaultEventParameters } from 'webtonative/Firebase/Analytics'
import { Dialogs, useDialogs } from '../Contexts/DialogsContext'
import { useGlobalContext } from '../Contexts/GlobalContext'
import SignIn from '../Dialogs/Forms/SignIn'
import SignUp from '../Dialogs/Forms/SignUp'
import { useNewContent } from '../hooks/useNewContent'
import { useScreenSize } from '../hooks/useScreenSize'
import theme from '../Theme/theme-v2'
import { reportSharedError } from '../utils/errorReporting'
import { AuxiliaryWrapper } from './AuxiliaryWrapper'
import { PiecesTheme } from './common/PiecesProvider'
import { clearQueryParams } from './common/queryParams'
import BurgerNavigation from './components/BurgerNavigation'
import UserMenu from './components/UserMenu'
import { getUserData } from './functions/ams-functions'
import { analyticsManager } from './services/analytics/AnalyticsManager'
import { getAppConfig } from './services/AppConfigService'

export type UserRatingData = {
    best: number
    best_date: string
    draw: number
    game_type: string
    is_provisional: boolean
    loss: number
    rating: number
    total_games: number
    user_id: string
    win: number
}

export type Settings = {
    autoAccept: boolean
    boardStyle: string
    coordinates: boolean
    dragAndDrop: boolean
    pieceStyle: PiecesTheme
    preMove: boolean
    soundCheckOn: boolean
    soundDrawOn: boolean
    soundMateOn: boolean
    soundMoveOn: boolean
    soundOn: boolean
    soundSmateOn: boolean
    autoQueen: boolean
    darkMode: boolean
    soundStyle: string
    friendlyMode: boolean
    showLegalMoves: boolean
    engineToggle: boolean
    evaluationBar: boolean
    highlightTopMoves: boolean
    multipleLines: number
}

export enum Rules {
    FULL_ACCESS = 'full_access',
    GAME_HISTORY = 'game_history',
    GAME_SETTINGS = 'game_settings',
    LIVE_GAMES = 'live_games',
    PROFILE = 'profile',
    NEWS = 'news',
    VIDEOS = 'videos',
    USER_MEMBERSHIPS = 'user_memberships',
    PLAY_BOTS = 'play_bots',
    FOLLOW = 'follow',
}

export enum GroupNames {
    GUEST = 'guest',
    MEMBER = 'member',
    SUBSCRIBED = 'subscribed',
}

export type UserGroup = {
    name: GroupNames
    id: number
    rules: [
        {
            // TODO: add rules as they are defined
            name: Rules
        },
    ]
}

export type UserData = {
    id: string
    user_name: string
    first_name?: string
    last_name?: string
    title: string | null
    avatar_url: string | null
    email: string
    birthday?: string
    phone_number?: string
    country?: string
    email_confirmed: boolean
    created_at: string
    over_12_years_old: boolean
    subscribed_to_mailing_list: boolean
    subscription_state: number
    subscription_days_left: number
    free_login: boolean
    ratings: {
        blitz: UserRatingData
        bullet: UserRatingData
        classic: UserRatingData
        rapid: UserRatingData
        puzzle?: UserRatingData
    }
    puzzleRating?: number
    ftue: {
        mode: 'guest' | 'user'
        newGame: boolean
        players: boolean
        computer: boolean
        analysis: boolean
        puzzle: boolean
        /** @deprecated not in use anymore */
        welcomeGuest: boolean
        /** @deprecated not in use anymore */
        welcomeUser: boolean
        puzzleQuest: boolean
    }
    newContent: {
        home: boolean
        play: boolean
        watch: boolean
        analysis: boolean
        puzzleQuest: boolean
        puzzles: boolean
        dailyPuzzle: boolean
        leaderboard: boolean
        videos: boolean
        news: boolean
        shop: boolean
        memberships: boolean
    }
    game_settings: Settings
    groups: Array<UserGroup>
    reward: {
        tier: number
        season: string
    }
    botd_reward_url?: string
    first_time_login: boolean
    user_config: {
        login_redirect_url: string
        main_menu_seniority: number
    }
}

export type FTUE = keyof Pick<UserData, 'ftue'>['ftue']

export type LoggedInUserData = {
    state: LoginState
    userData: UserData | null
    token: string
}

export enum AreaType {
    WEBSITE = 'website',
    PLAY_AREA = 'play_area',
}

export interface GlobalHeaderProps {
    extraButtons?: Array<JSX.Element>
    Link: any
    pathname: string
}

export enum LoginState {
    NOT_LOGGED_IN = 'not_logged_in',
    LOGGED_IN = 'logged_in',
    INITIALIZING = 'initializing',
}

function GlobalHeader(props: GlobalHeaderProps, ref: any) {
    const { extraButtons, Link, pathname } = props
    const { loginState, userData, setUserData } = useGlobalContext()
    const { registerDialog } = useDialogs()
    const { isMobile } = useScreenSize()
    const newContentProvider = useNewContent(pathname)

    useEffect(() => {
        if (loginState === LoginState.LOGGED_IN) {
            const fullUserData = getUserData()
            if (!!fullUserData) {
                setUserData(fullUserData.user)
            }
        }
    }, [loginState])

    // Cookie flush is gated on isAndroidORIosApp — a pre-JS SDK flag that's reliable at
    // first paint, unlike getAppConfig()?.isMobileApp, which is only ever set by the play
    // area's ?system=mobileApp deep link, so cold starts that miss it (and every website
    // URL, which has no such deep link at all) never flushed the Android cookie jar.
    // Deliberately its own effect: this is session-critical and must not be able to be
    // skipped by an unrelated native call throwing ahead of it.
    useEffect(() => {
        if (typeof window === 'undefined' || !isAndroidORIosApp) return
        // These methods only exist if the native shell injected them — an older wrapper
        // build (or one without the matching feature enabled) is missing them, and an
        // unguarded throw in an effect unmounts the whole app into the error boundary.
        try {
            forceUpdateCookies()
        } catch (error) {
            reportSharedError('forceUpdateCookies failed', error, { flow: 'auth' })
        }
    }, [userData?.id])

    useEffect(() => {
        // Left on the narrower isMobileApp gate on purpose: this is placeholder Firebase
        // wiring with hardcoded test payloads, unrelated to the cookie flush above, and
        // widening its reach would only spread junk telemetry.
        if (typeof window === 'undefined' || !getAppConfig()?.isMobileApp) return
        try {
            setCollection({
                enabled: true,
            })
            setDefaultEventParameters({
                parameters: {
                    level_name: 'Caverns01',
                    level_difficulty: 4,
                },
            })
            logScreen({
                screenName: 'Screen Name',
                screenClass: 'Screen Class',
            })
            logEvent({
                eventName: 'test',
                parameters: {
                    level_name: 'Caverns01',
                    level_difficulty: 4,
                },
            })
        } catch (error) {
            reportSharedError('Native Firebase Analytics call failed', error, { flow: 'analytics' })
        }
    }, [userData?.id])

    useEffect(() => {
        if (window.location) {
            const urlParams = new URLSearchParams(decodeURIComponent(window.location.search))
            const emailConfirmed = urlParams.get('emailConfirmed')
            if (emailConfirmed === 'true') {
                analyticsManager.dispatchEvent('emailActivated', {})
                clearQueryParams(['emailConfirmed'])
            }
        }
    }, [])

    useImperativeHandle(ref, () => ({}))

    return (
        <ThemeProvider theme={theme}>
            <Paper
                sx={{
                    px: isMobile ? '12px' : '40px',
                    py: isMobile ? '8px' : '15px',
                    backgroundColor: 'background.default',
                    position: 'sticky',
                    top: 0,
                    boxShadow: (theme) => `0px -1px 8px 6px ${alpha(theme.palette.background.default, 0.7)}`,
                    zIndex: 1300,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexGrow: 1,
                }}
            >
                <Stack direction="row" gap={2.5} alignItems="center">
                    <BurgerNavigation Link={Link} />
                    {/* <GlobalLink
                                Link={Link}
                                href={area === AreaType.PLAY_AREA ? websiteURL : '/'}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <img src={'/assets/images/logo.svg'} alt="logo" height="50px" width="50px" />
                            </GlobalLink> */}
                </Stack>
                <Stack direction="row" gap={2}>
                    <Stack direction="row" alignItems="center" gap={2}>
                        {extraButtons && extraButtons.map((button, index) => <Fragment key={index}>{button}</Fragment>)}
                    </Stack>

                    {userData && loginState === LoginState.LOGGED_IN && <UserMenu Link={Link} />}
                    {loginState === LoginState.NOT_LOGGED_IN && (
                        <Stack direction="row" gap={2}>
                            <Button
                                sx={{ minWidth: isMobile ? 'auto' : '200px' }}
                                variant="contained"
                                size={isMobile ? 'medium' : 'large'}
                                onClick={() => {
                                    registerDialog(Dialogs.SIGNIN, <SignIn />)
                                    analyticsManager.dispatchEvent('openLogin', {
                                        origin: 'header',
                                    })
                                }}
                            >
                                Log in
                            </Button>
                            <Button
                                sx={{ minWidth: isMobile ? 'auto' : '200px' }}
                                variant="contained"
                                size={isMobile ? 'medium' : 'large'}
                                onClick={() => {
                                    registerDialog(Dialogs.SIGNUP, <SignUp />)
                                    analyticsManager.dispatchEvent('openSignup', {
                                        origin: 'header',
                                    })
                                }}
                            >
                                Sign up
                            </Button>
                        </Stack>
                    )}
                    {loginState === LoginState.INITIALIZING && <CircularProgress />}
                </Stack>
            </Paper>
            {/* Wrapper is required to prevent some weird bug related to the files initialization */}
            {/* Contains any auxiliary components */}
            <AuxiliaryWrapper Link={Link} />
        </ThemeProvider>
    )
}

export default forwardRef(GlobalHeader)
